

import { useState, useEffect } from "react";
import SearchableDropdown from "../components/SearchableDropdown";

interface Vendor {
  id: number;
  name: string;
  commission_type?: string;
  commission_value?: number;
}

interface VendorItem {
  id?: number;
  vendor_id?: number;
  description?: string;
  quantity: number;
  unit?: string;
  unit_price: number;
  cost_price?: number;
  commission_percent?: number;
  commission_type?: string;
  notes?: string;
}

interface AddVendorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (vendor: VendorItem) => void;
  vendors: Vendor[];
  initialData?: VendorItem;
}

const INRIcon = ({ className }: { className?: string }) => (
  <span className={className}>₹</span>
);

export default function AddVendorModal({
  isOpen,
  onClose,
  onAdd,
  vendors,
  initialData,
}: AddVendorModalProps) {
  const [formData, setFormData] = useState<VendorItem>({
    vendor_id: undefined,
    description: "",
    quantity: 1,
    unit: "unit",
    unit_price: 0,
    cost_price: 0,
    commission_percent: 0,
    commission_type: "percentage",
    notes: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof VendorItem, string>>>({});
  const [commissionAutoFilled, setCommissionAutoFilled] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData({
          id: initialData.id,
          vendor_id: initialData.vendor_id,
          description: initialData.description || "",
          quantity: initialData.quantity || 1,
          unit: initialData.unit || "unit",
          unit_price: initialData.unit_price || 0,
          cost_price: initialData.cost_price || 0,
          commission_percent: initialData.commission_percent ?? 0,
          commission_type: initialData.commission_type || "percentage",
          notes: initialData.notes || "",
        });
      } else {
        setFormData({
          vendor_id: undefined,
          description: "",
          quantity: 1,
          unit: "unit",
          unit_price: 0,
          cost_price: 0,
          commission_percent: 0,
          commission_type: "percentage",
          notes: "",
        });
      }
      setErrors({});
      setCommissionAutoFilled(false);
    }
  }, [isOpen, initialData]);

  const handleChange = (field: keyof VendorItem, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleVendorSelect = (vendorId: string) => {
    const vendor = vendors.find(v => String(v.id) === vendorId);
    if (vendor) {
      const hasStoredCommission = (vendor.commission_value ?? 0) > 0;
      setFormData(prev => ({
        ...prev,
        vendor_id: vendor.id,
        commission_percent: vendor.commission_value ?? 0,
        commission_type: vendor.commission_type || "percentage",
      }));
      setCommissionAutoFilled(hasStoredCommission);
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof VendorItem, string>> = {};

    if (!formData.vendor_id) newErrors.vendor_id = "Vendor is required";
    if (!formData.description?.trim()) newErrors.description = "Service description is required";
    if (formData.quantity <= 0) newErrors.quantity = "Quantity must be greater than 0";
    if (formData.unit_price < 0) newErrors.unit_price = "Price cannot be negative";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      // Calculate and set cost_price before submitting
      const vendorCost = calculateCost();
      onAdd({
        ...formData,
        cost_price: vendorCost,
      });
    }
  };

  const calculateCost = () => {
    const unitPrice = formData.unit_price || 0;
    const commissionPercent = formData.commission_percent ?? 0;
    const commissionType = formData.commission_type || "percentage";

    let commission = 0;
    if (commissionType === "percentage") {
      commission = (unitPrice * commissionPercent) / 100;
    } else {
      commission = commissionPercent;
    }

    return unitPrice - commission;
  };

  const lineTotal = formData.quantity * formData.unit_price;
  const vendorCost = calculateCost();
  const totalVendorCost = vendorCost * formData.quantity;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-900/60 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-200 bg-gradient-to-r from-blue-50 to-white flex-shrink-0">
          <h3 className="text-lg font-bold text-stone-800">
            {initialData ? "Edit Vendor Assignment" : "Add Vendor Assignment"}
          </h3>
          <p className="text-xs text-stone-500 mt-1">Assign vendor with service details & pricing. Add multiple vendors as needed</p>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Vendor Selection */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-2">Vendor *</label>
            <SearchableDropdown
              options={vendors.map(v => ({ value: String(v.id), label: v.name }))}
              value={formData.vendor_id ? String(formData.vendor_id) : ""}
              onChange={handleVendorSelect}
              placeholder="Search and select vendor"
            />
            {errors.vendor_id && <p className="text-xs text-rose-500 mt-1">{errors.vendor_id}</p>}
          </div>

          {/* Service Description */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-2">Service Description *</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="e.g., Photography, Catering, Decoration"
              className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
            />
            {errors.description && <p className="text-xs text-rose-500 mt-1">{errors.description}</p>}
          </div>

          {/* Quantity & Unit */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-2">Quantity *</label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => handleChange("quantity", parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
              />
              {errors.quantity && <p className="text-xs text-rose-500 mt-1">{errors.quantity}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-2">Unit</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => handleChange("unit", e.target.value)}
                placeholder="e.g., hours, pieces"
                className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
              />
            </div>
          </div>

          {/* Unit Price */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-2">Unit Price (Client) *</label>
            <div className="relative">
              <INRIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 font-bold" />
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.unit_price}
                onChange={(e) => handleChange("unit_price", parseFloat(e.target.value) || 0)}
                className="w-full pl-6 pr-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
              />
            </div>
            {errors.unit_price && <p className="text-xs text-rose-500 mt-1">{errors.unit_price}</p>}
          </div>

          {/* Commission Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            {commissionAutoFilled && (
              <div className="flex items-center gap-1.5 mb-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Commission auto-filled from vendor profile
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold text-stone-700 mb-2">Commission Type</label>
                <select
                  value={formData.commission_type}
                  onChange={(e) => handleChange("commission_type", e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-blue-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (₹)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-stone-700 mb-2">
                  Commission {formData.commission_type === "percentage" ? "(%)" : "(₹)"}
                </label>
                <div className="relative">
                  {formData.commission_type === "fixed" && (
                    <INRIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 font-bold" />
                  )}
                  <input
                    type="number"
                    min="0"
                    step={formData.commission_type === "percentage" ? "1" : "0.01"}
                    max={formData.commission_type === "percentage" ? "100" : undefined}
                    value={formData.commission_percent ?? 0}
                    onChange={(e) => handleChange("commission_percent", parseFloat(e.target.value) || 0)}
                    className={`w-full ${formData.commission_type === "fixed" ? "pl-6" : "pl-3"} pr-3 py-2 rounded-lg border border-blue-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none`}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-lg p-2 border border-blue-100">
                <p className="text-xs text-stone-600 mb-0.5">Commission Amount</p>
                <p className="font-semibold text-stone-700">
                  ₹{((formData.unit_price * (formData.commission_percent ?? 0)) / (formData.commission_type === "percentage" ? 100 : 1)).toLocaleString('en-IN')}
                </p>
              </div>

              <div className="bg-white rounded-lg p-2 border border-blue-100">
                <p className="text-xs text-stone-600 mb-0.5">Vendor Cost (Per Unit)</p>
                <p className="font-semibold text-rose-600">
                  ₹{vendorCost.toLocaleString('en-IN')}
                </p>
              </div>
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-stone-600 mb-1">Client Total</p>
              <p className="text-lg font-bold text-amber-700">
                ₹{lineTotal.toLocaleString('en-IN')}
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
              <p className="text-xs text-stone-600 mb-1">Vendor Total</p>
              <p className="text-lg font-bold text-rose-600">
                ₹{totalVendorCost.toLocaleString('en-IN')}
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs text-stone-600 mb-1">Margin</p>
              <p className="text-lg font-bold text-green-600">
                ₹{(lineTotal - totalVendorCost).toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-2">Special Instructions</label>
            <textarea
              value={formData.notes || ""}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Any special requirements or notes..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-stone-200 bg-stone-50 px-6 py-3 flex gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg bg-white text-stone-600 font-semibold text-sm border border-stone-200 hover:bg-stone-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-colors"
          >
            {initialData ? "Update" : "Add"} Vendor
          </button>
        </div>
      </div>
    </div>
  );
}
